import React, { useState } from "react";
import {
  TextInput,
  View,
  Text,
  TextInputProps,
  StyleProp,
  ViewStyle,
} from "react-native";
import { IconButton } from "react-native-paper";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import { isIOS } from "../common/utils/platform.util";

type InputType = "text" | "email" | "number" | "password" | "phone";

interface AppInputProps {
  label?: string;
  value: string;
  onChange: (e: { nativeEvent: { text: string } }) => void;
  placeholder?: string;
  type?: InputType;
  error?: string;
  placeholderTextColor?: string;
  autoCapitalize?: TextInputProps["autoCapitalize"];
  multiline?: boolean;
  numberOfLines?: number;
  editable?: boolean;
  style?: StyleProp<ViewStyle>;
  inputStyle?: string;
}

const keyboardTypesMap: Record<InputType, TextInputProps["keyboardType"]> = {
  text: "default",
  email: "email-address",
  number: "numeric",
  phone: "phone-pad",
  password: "default", // handled by secureTextEntry
};

/**
 * A reusable input component for handling various types of text input fields.
 *
 * @param {string} [label] - The label displayed above the input field.
 * @param {string} value - The current value of the input field.
 * @param {(e: { nativeEvent: { text: string } }) => void} onChange - The function to call when the input value changes.
 * @param {string} [placeholder] - The placeholder text displayed when the input field is empty.
 * @param {InputType} [type="text"] - The type of input, which determines the keyboard type and secure text entry.
 * @param {string} [error=""] - The error message displayed below the input field.
 * @param {string} [placeholderTextColor="#8D8E90"] - The color of the placeholder text.
 * @param {TextInputProps["autoCapitalize"]} [autoCapitalize="none"] - The capitalization behavior of the input field.
 * @param {boolean} [multiline=false] - Whether the input field supports multiple lines of text.
 * @param {number} [numberOfLines=1] - The number of lines to display for a multiline input field.
 * @param {boolean} [editable=true] - Whether the input field is editable.
 * @param {StyleProp<ViewStyle>} [style={}] - Additional styles for the input container.
 * @param {string} [inputStyle=""] - Additional class names for the input field.
 */

const AppInput: React.FC<AppInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  error = "",
  placeholderTextColor = "#8D8E90",
  autoCapitalize = "none",
  multiline = false,
  numberOfLines = 1,
  editable = true,
  style = {},
  inputStyle = "",
}) => {
  const isSecure = type === "password";
  const [isVisible, setIsVisible] = useState(!isSecure);

  return (
    <View>
      {label && (
        <Text className="text-base font-semibold text-white">{label}</Text>
      )}

      <View>
        <TextInput
          value={value}
          placeholder={placeholder}
          placeholderTextColor={placeholderTextColor}
          keyboardType={keyboardTypesMap[type] || "default"}
          secureTextEntry={isSecure && !isVisible}
          autoCapitalize={autoCapitalize}
          onChange={onChange}
          editable={editable}
          multiline={multiline}
          numberOfLines={numberOfLines}
          className={`w-full bg-[#292D33] mt-2 p-3 text-white rounded-xl ${inputStyle}`}
          style={style}
        />

        {isSecure && (
          <IconButton
            icon={(props) => (
              <MaterialCommunityIcons
                name={isVisible ? "eye" : "eye-off"}
                {...props}
                style={{ color: "#01556F" }}
              />
            )}
            onPress={() => setIsVisible((prev) => !prev)}
            style={{
              position: "absolute",
              right: 0,
              top: isIOS ? 4 : 10,
            }}
          />
        )}
      </View>

      {!!error && <Text className="mt-1 text-sm text-red-400">{error}</Text>}
    </View>
  );
};

export default AppInput;
